import { useCallback } from 'react';

export default function usePdfSelectionAnchor({ currentPage }) {
  return useCallback((range, rawSelectedText) => {
    try {
      if (!range) return null;

      const selectedTextPreserved = String(rawSelectedText || '').replace(/^\s+|\s+$/g, '');
      if (!selectedTextPreserved) return null;

      const selection = window.getSelection();
      let normalizedRange = range;

      if (selection && selection.anchorNode && selection.focusNode) {
        const isBackward = selection.anchorNode === range.endContainer &&
          selection.focusNode === range.startContainer;

        const position = range.startContainer.compareDocumentPosition?.(range.endContainer);
        const isActuallyBackward = (position === 0 && range.startOffset > range.endOffset) ||
          (position & Node.DOCUMENT_POSITION_PRECEDING);

        if (isBackward || isActuallyBackward) {
          normalizedRange = document.createRange();
          normalizedRange.setStart(range.endContainer, range.endOffset);
          normalizedRange.setEnd(range.startContainer, range.startOffset);
        }
      }

      const pdfPage = normalizedRange.commonAncestorContainer.closest?.('[data-page-number]');
      let pageNumber = currentPage;
      if (pdfPage) {
        const pageNumAttr = pdfPage.getAttribute('data-page-number');
        if (pageNumAttr) {
          const parsed = parseInt(pageNumAttr, 10);
          if (!Number.isNaN(parsed)) pageNumber = parsed;
        }
      }

      let contextBefore = '';
      let contextAfter = '';
      try {
        const textLayer = normalizedRange.commonAncestorContainer.closest?.('.react-pdf__Page__textContent');
        if (textLayer) {
          const fullText = textLayer.textContent || '';
          const walker = document.createTreeWalker(textLayer, NodeFilter.SHOW_TEXT, null);

          let charCount = 0;
          let startPosition = -1;
          let endPosition = -1;
          let node;

          while ((node = walker.nextNode())) {
            if (node === normalizedRange.startContainer) {
              startPosition = charCount + normalizedRange.startOffset;
              break;
            }
            charCount += node.textContent.length;
          }

          walker.currentNode = textLayer;
          charCount = 0;
          while ((node = walker.nextNode())) {
            if (node === normalizedRange.endContainer) {
              endPosition = charCount + normalizedRange.endOffset;
              break;
            }
            charCount += node.textContent.length;
          }

          if (startPosition >= 0 && endPosition >= 0 && fullText) {
            const beforeStart = Math.max(0, startPosition - 50);
            const afterEnd = Math.min(fullText.length, endPosition + 50);
            contextBefore = fullText.slice(beforeStart, startPosition).trim();
            contextAfter = fullText.slice(endPosition, afterEnd).trim();
          }
        }
      } catch {
        // noop
      }

      return {
        page_number: pageNumber,
        selected_text: selectedTextPreserved,
        context_before: contextBefore,
        context_after: contextAfter,
      };
    } catch {
      return null;
    }
  }, [currentPage]);
}
